import { useEffect, useState, useMemo } from "react";
import { api, formatEUR, formatDate, KIND_LABELS, STATUS_LABELS, STATUS_COLORS } from "@/lib/diningApi";
import { useAuth } from "@/context/diningAuth";
import { useWaves } from "@/context/DiningWaveContext";
import { Mail, Check, ChevronDown, ChevronRight, FileText, AlertCircle, Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function gmailComposeUrl({ to, subject, body }) {
  const params = new URLSearchParams({ view: "cm", fs: "1", to: to || "", su: subject, body });
  return `https://mail.google.com/mail/?${params.toString()}`;
}

export default function Invoices() {
  const { user } = useAuth();
  const { currentWave } = useWaves();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const canEdit = isAdmin || user?.role === "operator";
  const [invoices, setInvoices] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [extras, setExtras] = useState({ previsto: 0, real: 0, items: [] });
  const [summary, setSummary] = useState(null);
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [issueDialog, setIssueDialog] = useState(null);
  const [payDialog, setPayDialog] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [tab, setTab] = useState("invoices"); // "invoices" | "extras"

  // Manual invoice create/edit dialog
  const [editorDialog, setEditorDialog] = useState(null);
  const emptyEditor = { restaurant_id: "", kind: "main", event_status: "confirmed", amount_due: 0, due_date: "", status: "pending", issue_date: "", expected_payment_date: "", paid_date: "", notes: "" };

  const openCreateDialog = (restaurantId = "") => {
    setEditorDialog({ ...emptyEditor, restaurant_id: restaurantId, due_date: new Date().toISOString().slice(0, 10), _editingId: null });
  };

  const openEditDialog = (inv) => {
    setEditorDialog({
      restaurant_id: inv.restaurant_id,
      kind: inv.kind,
      event_status: inv.event_status || "confirmed",
      amount_due: inv.amount_due,
      due_date: inv.due_date,
      status: inv.status,
      issue_date: inv.issue_date || "",
      expected_payment_date: inv.expected_payment_date || "",
      paid_date: inv.paid_date || "",
      notes: inv.notes || "",
      _editingId: inv.id,
    });
  };

  const saveInvoice = async () => {
    const d = editorDialog;
    if (!d.restaurant_id) return toast.error("Selecciona un restaurante");
    if (!d.amount_due || Number(d.amount_due) <= 0) return toast.error("Importe inválido");
    if (!d.due_date) return toast.error("Fecha de vencimiento obligatoria");
    const payload = {
      restaurant_id: d.restaurant_id,
      kind: d.kind,
      event_status: d.event_status || "confirmed",
      amount_due: Number(d.amount_due),
      due_date: d.due_date,
      status: d.status,
      issue_date: d.issue_date || null,
      expected_payment_date: d.expected_payment_date || null,
      paid_date: d.paid_date || null,
      notes: d.notes || "",
    };
    try {
      if (d._editingId) await api.put(`/invoices/${d._editingId}`, payload);
      else await api.post(`/invoices`, payload);
      toast.success(d._editingId ? "Factura actualizada" : "Factura creada");
      setEditorDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm("¿Eliminar esta factura definitivamente?")) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Factura eliminada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const load = async () => {
    const [rs, inv, ex, sm] = await Promise.all([
      api.get("/restaurants").then(({ data }) => data),
      api.get("/invoices").then(({ data }) => data),
      api.get("/invoices/extras").then(({ data }) => data).catch(() => ({ previsto: 0, real: 0, items: [] })),
      api.get("/billing/summary").then(({ data }) => data).catch(() => null),
    ]);
    setRestaurants(rs); setInvoices(inv); setExtras(ex); setSummary(sm);
  };
  useEffect(() => { load(); }, []);

  const restMap = useMemo(() => Object.fromEntries(restaurants.map(r => [r.id, r])), [restaurants]);

  const updateInvoice = async (inv, fields) => {
    await api.put(`/invoices/${inv.id}`, fields);
    toast.success("Factura actualizada"); load();
  };

  const requestMail = (inv) => {
    const r = restMap[inv.restaurant_id];
    const subject = `Solicitud de factura — ${KIND_LABELS[inv.kind]} — ${r?.name || ""}`;
    const body = `Hola${r?.contact_name ? " " + r.contact_name : ""},\n\nTe escribo para solicitar la factura correspondiente a:\n\n· Concepto: ${KIND_LABELS[inv.kind]}\n· Importe (IVA incluido): ${formatEUR(inv.amount_due)}\n· Restaurante: ${r?.name}\n· Fecha de vencimiento: ${formatDate(inv.due_date)}\n\nMuchas gracias.`;
    window.open(gmailComposeUrl({ to: r?.contact_email || "", subject, body }), "_blank");
    if (inv.status === "pending" || inv.status === "overdue") updateInvoice(inv, { status: "requested" });
  };

  // Filter out extras from main invoices list (they have their own tab)
  const mainInvoices = useMemo(() => invoices.filter((i) => i.kind !== "extra"), [invoices]);

  // Group invoices by city > restaurant
  const grouped = useMemo(() => {
    const filtered = mainInvoices.filter((i) => restaurantFilter === "all" || i.restaurant_id === restaurantFilter);
    const byCity = {};
    filtered.forEach((i) => {
      const r = restMap[i.restaurant_id];
      const city = r?.city || "Sin ciudad";
      const rid = i.restaurant_id;
      byCity[city] = byCity[city] || {};
      byCity[city][rid] = byCity[city][rid] || { restaurant: r, invoices: [] };
      byCity[city][rid].invoices.push(i);
    });
    // Compute totals
    const out = Object.entries(byCity).map(([city, rs]) => {
      const rests = Object.values(rs).map((g) => {
        const totals = { pending: 0, requested: 0, issued: 0, paid: 0, overdue: 0, total_outstanding: 0, total_paid: 0 };
        g.invoices.forEach((i) => {
          totals[i.status] = (totals[i.status] || 0) + i.amount_due;
          if (["pending", "requested", "issued", "overdue"].includes(i.status)) {
            totals.total_outstanding += i.amount_due;
          } else if (i.status === "paid") {
            totals.total_paid += i.amount_due;
          }
        });
        return { ...g, totals };
      }).sort((a, b) => (a.restaurant?.name || "").localeCompare(b.restaurant?.name || ""));
      const cityOutstanding = rests.reduce((s, r) => s + r.totals.total_outstanding, 0);
      const cityPaid = rests.reduce((s, r) => s + r.totals.total_paid, 0);
      return { city, restaurants: rests, cityOutstanding, cityPaid };
    }).sort((a, b) => a.city.localeCompare(b.city));
    return out;
  }, [mainInvoices, restMap, restaurantFilter]);

  const totals = useMemo(() => {
    const t = { pending: 0, requested: 0, issued: 0, paid: 0, overdue: 0 };
    mainInvoices.forEach((i) => { t[i.status] = (t[i.status] || 0) + i.amount_due; });
    return t;
  }, [mainInvoices]);

  // Group extras by restaurant: include ALL restaurants with forecast,
  // not only those with attendance recorded. by_restaurant comes from backend.
  const extrasGrouped = useMemo(() => {
    const items = Array.isArray(extras) ? extras : (extras?.items || []);
    const rowsByRest = {};
    items.forEach((x) => {
      rowsByRest[x.restaurant_id] = rowsByRest[x.restaurant_id] || [];
      rowsByRest[x.restaurant_id].push(x);
    });
    Object.values(rowsByRest).forEach((arr) => arr.sort((a, b) => a.date.localeCompare(b.date)));

    const byRestBackend = (extras && Array.isArray(extras.by_restaurant)) ? extras.by_restaurant : [];
    const list = byRestBackend.map((b) => ({
      restaurant_id: b.restaurant_id,
      name: b.restaurant_name,
      city: b.city,
      previsto: Number(b.previsto || 0),
      previsto_confirmed: Number(b.previsto_confirmed || 0),
      previsto_late: Number(b.previsto_late || 0),
      real: Number(b.real || 0),
      total_pax: Number(b.pax_extras || 0),
      total_amount: Number(b.real || 0),
      events: Number(b.events || 0),
      events_confirmed: Number(b.events_confirmed || 0),
      events_late: Number(b.events_late || 0),
      events_with_real: Number(b.events_with_real || 0),
      rows: rowsByRest[b.restaurant_id] || [],
    }));
    return {
      list,
      grandPax: list.reduce((s, g) => s + g.total_pax, 0),
      grandAmount: Math.round(list.reduce((s, g) => s + g.real, 0) * 100) / 100,
      previsto: Number(extras?.previsto || 0),
      previsto_confirmed: Number(extras?.previsto_confirmed || 0),
      previsto_late: Number(extras?.previsto_late || 0),
      real: Number(extras?.real || 0),
    };
  }, [extras]);

  // Find extra invoice by restaurant id (used to render its current status / mark as paid)
  const extraInvoiceFor = (rid) => invoices.find((i) => i.restaurant_id === rid && i.kind === "extra");

  // Map: restaurant_id -> billing/summary entry (split by status)
  const restSummaryMap = useMemo(() => {
    const m = {};
    (summary?.invoices?.by_restaurant || []).forEach((r) => { m[r.restaurant_id] = r; });
    return m;
  }, [summary]);

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="invoices-page">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Tracking · {currentWave?.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Facturas</h1>
        </div>
        {isAdmin && (
          <Button onClick={() => openCreateDialog()} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="invoice-new-button">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nueva factura
          </Button>
        )}
      </div>

      <div className="flex gap-1 mb-8 border-b border-zinc-200">
        <button
          onClick={() => setTab("invoices")}
          data-testid="tab-invoices"
          className={`px-4 py-2 text-sm uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === "invoices" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
        >
          <FileText className="h-4 w-4" strokeWidth={1.5} /> Facturas (anticipo · principal)
        </button>
        <button
          onClick={() => setTab("extras")}
          data-testid="tab-extras"
          className={`px-4 py-2 text-sm uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === "extras" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
        >
          <AlertCircle className="h-4 w-4" strokeWidth={1.5} /> Excedentes (no-show)
          {extrasGrouped.grandPax > 0 && <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-mono">{extrasGrouped.grandPax}</span>}
        </button>
      </div>

      {tab === "invoices" && (
        <>
      <div className="grid grid-cols-5 gap-3 mb-8">
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <div
            key={k}
            data-testid={`filter-${k}`}
            className="text-left bg-white border border-zinc-200 p-4"
          >
            <p className="label-eyebrow">{label}</p>
            <p className="font-heading text-xl mt-2 font-mono">{formatEUR(totals[k] || 0)}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 max-w-xs">
        <Label className="text-xs uppercase tracking-wider">Restaurante</Label>
        <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
          <SelectTrigger className="rounded-none" data-testid="restaurant-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none max-h-80">
            <SelectItem value="all">Todos los restaurantes</SelectItem>
            {[...restaurants]
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`restaurant-filter-option-${r.id}`}>
                  {r.name}{r.city ? ` · ${r.city}` : ""}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-6">
        {grouped.length === 0 && <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500">Sin facturas.</div>}
        {grouped.map((cityGroup) => (
          <div key={cityGroup.city} className="bg-white border border-zinc-200" data-testid={`city-block-${cityGroup.city}`}>
            <div className="p-5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div>
                <p className="label-eyebrow">{cityGroup.city}</p>
                <h3 className="font-heading text-xl mt-1">{cityGroup.restaurants.length} restaurantes</h3>
              </div>
              <div className="flex gap-8 text-right">
                <div>
                  <p className="label-eyebrow">Por pagar</p>
                  <p className="font-mono font-heading text-xl mt-1">{formatEUR(cityGroup.cityOutstanding)}</p>
                </div>
                <div>
                  <p className="label-eyebrow text-emerald-700">Pagado</p>
                  <p className="font-mono font-heading text-xl mt-1 text-emerald-700">{formatEUR(cityGroup.cityPaid)}</p>
                </div>
              </div>
            </div>
            <div className="divide-y divide-zinc-100">
              {cityGroup.restaurants.map((rg) => {
                const rid = rg.restaurant?.id;
                const isCollapsed = collapsed[rid];
                return (
                  <div key={rid} data-testid={`rest-block-${rid}`}>
                    <div className="w-full px-6 py-3 flex items-center justify-between hover:bg-zinc-50 text-left">
                      <button onClick={() => setCollapsed({ ...collapsed, [rid]: !isCollapsed })} className="flex items-center gap-2 flex-1 text-left">
                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-400" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />}
                        <p className="font-medium">{rg.restaurant?.name}</p>
                        <p className="text-xs text-zinc-500 font-mono">({rg.invoices.length})</p>
                        {rg.restaurant?.billing_scheme === "tatel_advance" && (
                          <span data-testid={`tatel-badge-${rid}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 font-mono" title="1 factura por evento · 95% × aforo máx × precio · vencimiento 15 días antes del evento">
                            Tatel · 95% · 15d
                          </span>
                        )}
                      </button>
                      <div className="flex gap-6 text-right text-sm items-center">
                        <div className="font-mono">{formatEUR(rg.totals.total_outstanding)} <span className="text-xs text-zinc-500 uppercase tracking-wider">por pagar</span></div>
                        {rg.totals.total_paid > 0 && (
                          <div className="font-mono text-emerald-700">{formatEUR(rg.totals.total_paid)} <span className="text-xs uppercase tracking-wider">pagado</span></div>
                        )}
                        {isAdmin && (
                          <button onClick={(ev) => { ev.stopPropagation(); openCreateDialog(rid); }} data-testid={`add-inv-${rid}`} className="text-xs px-2 py-1 border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 uppercase tracking-wider inline-flex items-center gap-1">
                            <Plus className="h-3 w-3" strokeWidth={1.5} /> Factura
                          </button>
                        )}
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="bg-zinc-50/30">
                        {restSummaryMap[rid] && (
                          <div className="px-6 py-3 border-t border-zinc-100 grid grid-cols-2 gap-3 bg-zinc-50" data-testid={`split-${rid}`}>
                            <div className="bg-white border border-emerald-200 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <p className="label-eyebrow text-emerald-700">Fechas confirmadas</p>
                                <span className="text-xs font-mono text-emerald-700">{restSummaryMap[rid].confirmed.events} ev.</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-zinc-500 uppercase tracking-wider">Anticipo 10% fechas confirmadas</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed.anticipo)}</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-zinc-500 uppercase tracking-wider">Principal 90% fechas confirmadas</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed.principal)}</span>
                              </div>
                              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-100 font-medium">
                                <span>Subtotal</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed.anticipo + restSummaryMap[rid].confirmed.principal)}</span>
                              </div>
                            </div>
                            <div className={`bg-white border p-3 ${restSummaryMap[rid].confirmed_late.events > 0 ? "border-purple-300" : "border-zinc-200 opacity-70"}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="label-eyebrow text-purple-700">Nuevas confirmadas</p>
                                <span className="text-xs font-mono text-purple-700">{restSummaryMap[rid].confirmed_late.events} ev.</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-zinc-500 uppercase tracking-wider">Anticipo 10% nuevas confirmadas</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed_late.anticipo)}</span>
                              </div>
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-zinc-500 uppercase tracking-wider">Principal 90% nuevas confirmadas</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed_late.principal)}</span>
                              </div>
                              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-zinc-100 font-medium">
                                <span>Subtotal</span>
                                <span className="font-mono">{formatEUR(restSummaryMap[rid].confirmed_late.anticipo + restSummaryMap[rid].confirmed_late.principal)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {(() => {
                          // Sort invoices: confirmed first, then confirmed_late; within each, anticipo > main > extra
                          const kindOrder = { anticipo: 0, main: 1, extra: 2 };
                          const statusOrder = { confirmed: 0, confirmed_late: 1 };
                          const sorted = [...rg.invoices].sort((a, b) => {
                            const sa = statusOrder[a.event_status || "confirmed"] ?? 0;
                            const sb = statusOrder[b.event_status || "confirmed"] ?? 0;
                            if (sa !== sb) return sa - sb;
                            return (kindOrder[a.kind] ?? 9) - (kindOrder[b.kind] ?? 9);
                          });
                          return sorted.map((i) => {
                            const es = i.event_status || "confirmed";
                            const subLabel = i.kind === "extra"
                              ? "EXCEDENTES"
                              : (es === "confirmed_late" ? "NUEVAS CONFIRMADAS" : "FECHAS CONFIRMADAS");
                            const subLabelColor = i.kind === "extra"
                              ? "text-zinc-500"
                              : (es === "confirmed_late" ? "text-purple-700" : "text-emerald-700");
                          return (
                          <div key={i.id} className="grid grid-cols-12 px-6 py-3 border-t border-zinc-100 items-center text-sm hover:bg-white" data-testid={`invoice-${i.id}`}>
                            <div className="col-span-3">
                              <div>{KIND_LABELS[i.kind]}</div>
                              <div className={`label-eyebrow mt-0.5 ${subLabelColor}`}>{subLabel}</div>
                            </div>
                            <div className="col-span-3 text-xs font-mono text-zinc-600">
                              Vence {formatDate(i.due_date)}
                              {i.issue_date && <span className="text-violet-700"> · Emitida {formatDate(i.issue_date)}</span>}
                              {i.expected_payment_date && <span className="text-blue-700"> · Pago prev. {formatDate(i.expected_payment_date)}</span>}
                              {i.paid_date && <span className="text-emerald-700"> · Pagada {formatDate(i.paid_date)}</span>}
                            </div>
                            <div className="col-span-2 font-mono">{formatEUR(i.amount_due)}</div>
                            <div className="col-span-1">
                              <span className={`text-xs px-2 py-1 border ${STATUS_COLORS[i.status]} uppercase tracking-wider`}>{STATUS_LABELS[i.status]}</span>
                            </div>
                            <div className="col-span-3 flex justify-end gap-2 flex-wrap">
                              {isAdmin && (i.status === "pending" || i.status === "overdue" || i.status === "requested") && (
                                <button onClick={() => requestMail(i)} data-testid={`req-${i.id}`} className="inline-flex items-center text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50">
                                  <Mail className="h-3 w-3 mr-1" strokeWidth={1.5} /> {i.status === "requested" ? "Reenviar" : "Solicitar"}
                                </button>
                              )}
                              {isAdmin && (i.status === "requested" || i.status === "pending" || i.status === "overdue") && (
                                <button onClick={() => setIssueDialog({ invoice: i, issue_date: new Date().toISOString().slice(0,10) })} data-testid={`issued-${i.id}`} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 uppercase tracking-wider">Emitida</button>
                              )}
                              {canEdit && (i.status === "issued") && (
                                <button onClick={() => setPayDialog({ invoice: i, paid_date: new Date().toISOString().slice(0,10) })} data-testid={`paid-${i.id}`} className="text-xs px-2 py-1 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 uppercase tracking-wider">
                                  <Check className="h-3 w-3 mr-1 inline" strokeWidth={1.5} /> Marcar pagada
                                </button>
                              )}
                              {isAdmin && (
                                <>
                                  <button onClick={() => openEditDialog(i)} data-testid={`edit-${i.id}`} className="p-1 hover:bg-zinc-100" title="Editar factura"><Edit className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.5} /></button>
                                  <button onClick={() => removeInvoice(i)} data-testid={`delete-${i.id}`} className="p-1 hover:bg-red-50" title="Eliminar factura"><Trash2 className="h-3.5 w-3.5 text-zinc-500 hover:text-red-600" strokeWidth={1.5} /></button>
                                </>
                              )}
                            </div>
                          </div>
                          );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
        </>
      )}

      {tab === "extras" && (
        <div className="space-y-6" data-testid="extras-tab">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border-2 border-zinc-200 p-5" data-testid="extras-prevision">
              <p className="label-eyebrow text-zinc-500">Previsión de non-show</p>
              <p className="text-xs text-zinc-500 mt-2 max-w-md">Importe máximo posible si todos los eventos confirmados llenan hasta el aforo máximo.</p>
              <p className="font-mono font-heading text-3xl mt-4 text-zinc-900">{formatEUR(extrasGrouped.previsto || 0)}</p>
              <div className="mt-3 pt-3 border-t border-zinc-100 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="label-eyebrow text-emerald-700">Fechas confirmadas</p>
                  <p className="font-mono text-sm mt-1">{formatEUR(extrasGrouped.previsto_confirmed || 0)}</p>
                </div>
                <div>
                  <p className="label-eyebrow text-purple-700">Nuevas confirmadas</p>
                  <p className="font-mono text-sm mt-1">{formatEUR(extrasGrouped.previsto_late || 0)}</p>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 border-2 border-purple-300 p-5" data-testid="extras-real">
              <p className="label-eyebrow text-purple-700">Importe real non-show</p>
              <p className="text-xs text-purple-800 mt-2 max-w-md">Excedentes sobre el aforo mínimo registrados en asistencia hasta la fecha.</p>
              <p className="font-mono font-heading text-3xl mt-4 text-purple-900">{formatEUR(extrasGrouped.real || 0)}</p>
              <p className="text-xs text-purple-700 mt-2 font-mono">{extrasGrouped.grandPax || 0} pax extras · {extrasGrouped.previsto > 0 ? Math.round((extrasGrouped.real / extrasGrouped.previsto) * 100) : 0}% de la previsión</p>
            </div>
          </div>

          {extrasGrouped.list.length === 0 && (
            <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="no-extras">
              No hay eventos confirmados todavía. Cuando se confirmen, aparecerá aquí la previsión por restaurante.
            </div>
          )}

          {extrasGrouped.list.map((g) => {
            const exInv = extraInvoiceFor(g.restaurant_id);
            const pct = g.previsto > 0 ? Math.round((g.real / g.previsto) * 100) : 0;
            const hasReal = g.rows.length > 0;
            return (
              <div key={g.restaurant_id} className="bg-white border border-zinc-200" data-testid={`extras-rest-${g.restaurant_id}`}>
                <div className="p-5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="label-eyebrow">{g.city}</p>
                    <h3 className="font-heading text-xl mt-1">{g.name}</h3>
                    <p className="text-xs text-zinc-500 mt-1 font-mono">{g.events} eventos confirmados · {g.events_with_real} con asistencia registrada</p>
                  </div>
                  <div className="flex gap-6 text-right items-center flex-wrap">
                    <div>
                      <p className="label-eyebrow">Previsión non-show</p>
                      <p className="font-mono text-lg mt-1 text-zinc-900">{formatEUR(g.previsto)}</p>
                      <div className="flex gap-3 mt-1 justify-end text-[10px]">
                        <span className="font-mono">
                          <span className="text-emerald-700">Conf.</span> {formatEUR(g.previsto_confirmed)}
                          <span className="text-zinc-400"> ({g.events_confirmed})</span>
                        </span>
                        <span className="font-mono">
                          <span className="text-purple-700">Nuevas</span> {formatEUR(g.previsto_late)}
                          <span className="text-zinc-400"> ({g.events_late})</span>
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="label-eyebrow text-purple-700">Importe real</p>
                      <p className="font-mono text-lg mt-1 text-purple-800">{formatEUR(g.real)}</p>
                      <p className="text-[10px] text-purple-700 font-mono mt-0.5">{g.total_pax || 0} pax · {pct}%</p>
                    </div>
                    {exInv && (
                      <>
                        <div>
                          <p className="label-eyebrow">Factura</p>
                          <span className={`text-xs px-2 py-1 border ${STATUS_COLORS[exInv.status]} uppercase tracking-wider mt-1 inline-block`}>{STATUS_LABELS[exInv.status]}</span>
                          {exInv.paid_date && <p className="text-xs text-emerald-700 font-mono mt-1">Pagada {formatDate(exInv.paid_date)}</p>}
                          {exInv.expected_payment_date && !exInv.paid_date && <p className="text-xs text-blue-700 font-mono mt-1">Pago prev. {formatDate(exInv.expected_payment_date)}</p>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {isAdmin && (exInv.status === "pending" || exInv.status === "overdue" || exInv.status === "requested") && (
                            <button onClick={() => requestMail(exInv)} data-testid={`extra-req-${exInv.id}`} className="inline-flex items-center text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50">
                              <Mail className="h-3 w-3 mr-1" strokeWidth={1.5} /> Solicitar
                            </button>
                          )}
                          {isAdmin && (exInv.status === "pending" || exInv.status === "overdue" || exInv.status === "requested") && (
                            <button onClick={() => setIssueDialog({ invoice: exInv, issue_date: new Date().toISOString().slice(0,10) })} data-testid={`extra-issued-${exInv.id}`} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 uppercase tracking-wider">Emitida</button>
                          )}
                          {canEdit && exInv.status === "issued" && (
                            <button onClick={() => setPayDialog({ invoice: exInv, paid_date: new Date().toISOString().slice(0,10) })} data-testid={`extra-paid-${exInv.id}`} className="text-xs px-2 py-1 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 uppercase tracking-wider">
                              <Check className="h-3 w-3 mr-1 inline" strokeWidth={1.5} /> Marcar pagada
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {hasReal ? (
                  <>
                    <div className="grid grid-cols-12 px-6 py-2 border-b border-zinc-100 text-xs uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50/50">
                      <div className="col-span-3">Fecha</div>
                      <div className="col-span-2">Servicio</div>
                      <div className="col-span-1">Turnos</div>
                      <div className="col-span-2">Reales / Mín / Máx</div>
                      <div className="col-span-2">Pax extras</div>
                      <div className="col-span-2 text-right">Importe</div>
                    </div>
                    {g.rows.map((x) => (
                      <div key={x.event_id} className="grid grid-cols-12 px-6 py-2.5 border-b border-zinc-100 items-center text-sm hover:bg-zinc-50" data-testid={`extra-row-${x.event_id}`}>
                        <div className="col-span-3 font-mono">{formatDate(x.date)}</div>
                        <div className="col-span-2">{x.meal_type === "lunch" ? "Comida" : "Cena"}</div>
                        <div className="col-span-1 font-mono">x{x.sessions}</div>
                        <div className="col-span-2 font-mono text-xs">
                          <span className="text-zinc-900">{x.attendees_real}</span>
                          <span className="text-zinc-400"> · {x.pax_min} · {x.pax_max}</span>
                        </div>
                        <div className="col-span-2 font-mono text-purple-800">+{x.extra_pax}</div>
                        <div className="col-span-2 font-mono text-right text-purple-800">{formatEUR(x.extra_amount)}</div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="px-6 py-4 text-xs text-zinc-500 italic" data-testid={`extras-empty-${g.restaurant_id}`}>
                    Aún sin asistencia registrada. La previsión refleja el escenario máximo (todos los turnos llenos al aforo máximo).
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editorDialog} onOpenChange={(v) => !v && setEditorDialog(null)}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editorDialog?._editingId ? "Editar factura" : "Nueva factura"}</DialogTitle>
            <DialogDescription>{editorDialog?._editingId ? "Modifica los datos de la factura. Los cambios se aplican inmediatamente." : "Crea una factura manual para este restaurante (anticipo, principal, excedente u otra)."}</DialogDescription>
          </DialogHeader>
          {editorDialog && (
            <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Restaurante</Label>
                  <Select value={editorDialog.restaurant_id} onValueChange={(v) => setEditorDialog({ ...editorDialog, restaurant_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="editor-restaurant"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Tipo</Label>
                  <Select value={editorDialog.kind} onValueChange={(v) => setEditorDialog({ ...editorDialog, kind: v })}>
                    <SelectTrigger className="rounded-none" data-testid="editor-kind"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="anticipo">Anticipo (10%)</SelectItem>
                      <SelectItem value="main">Principal (90%)</SelectItem>
                      <SelectItem value="extra">Excedente / no-show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Fechas asociadas</Label>
                <Select value={editorDialog.event_status} onValueChange={(v) => setEditorDialog({ ...editorDialog, event_status: v })}>
                  <SelectTrigger className="rounded-none" data-testid="editor-event-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="confirmed">Fechas confirmadas</SelectItem>
                    <SelectItem value="confirmed_late">Nuevas confirmadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Importe (€)</Label>
                  <Input type="number" step="0.01" value={editorDialog.amount_due} onChange={(e) => setEditorDialog({ ...editorDialog, amount_due: e.target.value })} className="rounded-none" data-testid="editor-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Vencimiento</Label>
                  <Input type="date" value={editorDialog.due_date} onChange={(e) => setEditorDialog({ ...editorDialog, due_date: e.target.value })} className="rounded-none" data-testid="editor-due" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Estado</Label>
                <Select value={editorDialog.status} onValueChange={(v) => setEditorDialog({ ...editorDialog, status: v })}>
                  <SelectTrigger className="rounded-none" data-testid="editor-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Fecha emisión</Label>
                  <Input type="date" value={editorDialog.issue_date || ""} onChange={(e) => setEditorDialog({ ...editorDialog, issue_date: e.target.value })} className="rounded-none" data-testid="editor-issue" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Pago previsto</Label>
                  <Input type="date" value={editorDialog.expected_payment_date || ""} onChange={(e) => setEditorDialog({ ...editorDialog, expected_payment_date: e.target.value })} className="rounded-none" data-testid="editor-expected" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Pagada el</Label>
                  <Input type="date" value={editorDialog.paid_date || ""} onChange={(e) => setEditorDialog({ ...editorDialog, paid_date: e.target.value })} className="rounded-none" data-testid="editor-paid" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Notas</Label>
                <Input value={editorDialog.notes} onChange={(e) => setEditorDialog({ ...editorDialog, notes: e.target.value })} className="rounded-none" data-testid="editor-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveInvoice} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="editor-save">{editorDialog?._editingId ? "Guardar cambios" : "Crear factura"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!issueDialog} onOpenChange={(v) => !v && setIssueDialog(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Marcar como emitida</DialogTitle>
            <DialogDescription>Introduce la fecha de la factura y cuándo está previsto pagarla.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">Fecha de factura</Label>
              <Input type="date" value={issueDialog?.issue_date || ""} onChange={(e) => setIssueDialog({ ...issueDialog, issue_date: e.target.value })} className="rounded-none" data-testid="issue-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Fecha prevista de pago</Label>
              <Input type="date" value={issueDialog?.expected_payment_date || ""} onChange={(e) => setIssueDialog({ ...issueDialog, expected_payment_date: e.target.value })} className="rounded-none" data-testid="expected-payment-input" />
              <p className="text-xs text-zinc-500 mt-1">Tu compañera de administración usará esta fecha para programar el pago.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={async () => { await updateInvoice(issueDialog.invoice, { status: "issued", issue_date: issueDialog.issue_date, expected_payment_date: issueDialog.expected_payment_date }); setIssueDialog(null); }} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="issue-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!payDialog} onOpenChange={(v) => !v && setPayDialog(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Marcar como pagada</DialogTitle>
            <DialogDescription>Fecha de pago efectivo.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider">Fecha de pago</Label>
            <Input type="date" value={payDialog?.paid_date || ""} onChange={(e) => setPayDialog({ ...payDialog, paid_date: e.target.value })} className="rounded-none" data-testid="paid-date-input" />
          </div>
          <DialogFooter>
            <Button onClick={async () => { await updateInvoice(payDialog.invoice, { status: "paid", paid_date: payDialog.paid_date }); setPayDialog(null); }} className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="paid-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
